import { Commit } from 'vuex';
import {
  requestBuyList,
  requestBuyDetail,
  ReqRequestBuyDetail,
  ReqRequestBuyList,
} from '../../apis/orderApis';
import {
  EquityBrief,
  PayOrderInfo,
  OrderEquity,
} from "./ticket";

/** 订单状态 */
export const OrderStatus = {
  /** 已冻结 */
  FREEZE: -1,
  /** 待支付 */
  PAY: 0,
  /** 已支付 */
  PAID: 1,
  /** 已退款 */
  REFUND: 2,
  /** 已过期 */
  OUTDATE: 3,
  /** 已取消 */
  // CANCEL: 4,
  /** 进行中 */
  ONGOING: 5,
} as const;

// TODO 卡券：这里原来写在页面中src/view/pages/Order_New/consts/index.ts
export type OrderStatusType = typeof OrderStatus[keyof typeof OrderStatus];

export type OrderVirtualData = {
  virtualName: string
  startTime: string // 开始时间
  endTime: string // 结束时间
}

export type OrderData = {
  orderId: string // 订单编号
  mobile: string // 用户手机
  payAmount: number // 用户手机
  goodsName: string // 商品名称
  orderStatus: number // 订单状态
  payStatus: number // 待支付状态
  orderNum: number // 下单数量
  orderMoney: string // 实付金额
  remainNum: number // 剩余数量
  usedNum: number // 已使用数量
  expiredNum: number // 已过期数量
  remainNumStr: string // 剩余数量
  orderTime: string // 下单时间
  payTime: string // 付款时间
  useNotice: string // 用户须知
  refundTime: string // 退款时间
  deadlineTime: string // 过期时间
  virtualList: OrderEquity[] // 券批次列表
  categoryName: string // 优惠券种类名称
  categoryId: string // 优惠券种类id
  skuId: string
  spuId: string
  originalPrice: string
  salePrice: string
  payOrder: PayOrderInfo
  /** 奖励金抵扣 */
  pointDiscountAmount?: number;
  /** 是否支持退款 */
  orderCanRefund: boolean;
  orderSource?: string;
  picUrl: string;
  shortRemark: string;
  /** 优惠总金额 */
  totalPromoAmount?: number;
  /** 优惠信息 */
  totalPromotionInfos?: {
    /** 券ID */
    promotionId: string; // 券ID,当couponPromoType=1时存在
    /** 券名称 */
    promotionName: string; // 券名称
    /** 卡券商城优惠类型， 1-营销优惠 2-奖励金 */
    couponPromoType: number; // 卡券商城优惠类型， 1-营销优惠 2-奖励金
    /** 优惠金额 */
    worth: number; // 优惠金额
    /** 可用积分数量，当couponPromoType=2时存在 */
    points: number;
  }[];
}

export type OrderShortData = {
  orderId: string // 订单标号
  goodsName: string // 商品名称
  name: string // 类目名
  orderStatus: number // 订单状态 为0表示待支付状态
  orderSource: number // 订单来源 为0或者为空则是优惠券商城内部订单，否则可能为第三方订单或者其他
  /** 原价 */
  originalPrice: number;
  /** 实价 */
  salePrice: number;
  orderTime: string // 购买时间
  status: number // 订单状态
  statusStr: string // 订单状态描述
  skuId: string
  spuId: string
  payOrder: PayOrderInfo // 订单信息
  categoryId: string // 优惠券种类id
  /** 业务线名称 */
  categoryName: string;
  /** 2 为拼团 */
  priceType?: number;
  /** 拼团数据 */
  groupRecordInfo?: {
    itemAliasName: string;
    originPrice: number;
    groupPrice: number;
    groupStatus: number;
    expiredTime: number;
    groupNum: number;
    joinedNum: number;
    activityUrl: string;
    groupGuid: string;
    activityCode: string;
    groupTag: string;
  };
  picUrl: string;
  listPicUrl: string;
  virtualList: EquityBrief[];
  /** 下单商品数量 */
  num: number;
  /** 用户须知 */
  useNotice: string;
  /** 优惠说明 */
  promoDescription: string;
  /** 可用范围 */
  usableRange: string;
  /** 优惠信息 */
  promotionInfos?: {
    /** 券ID */
    promotionId: string; // 券ID,当couponPromoType=1时存在
    /** 券名称 */
    promotionName: string; // 券名称
    /** 卡券商城优惠类型， 1-营销优惠 2-奖励金 */
    couponPromoType: number; // 卡券商城优惠类型， 1-营销优惠 2-奖励金
    /** 优惠金额 */
    worth: number; // 优惠金额
    /** 可用积分数量，当couponPromoType=2时存在 */
    points: number;
  }[];
}

/** 新订单列表项 */
export interface OrderListItem {
  showGoodsName: string;
  /** 订单号 */
  orderId: string;
  /** 订单状态 为0表示待支付状态 */
  orderStatus: OrderStatusType;
  /** 订单来源 为0或者为空则是优惠券商城内部订单，否则可能为第三方订单或者其他 */
  orderSource: number;
  /** 购买时间戳 */
  orderTime: string;
  /** 购买时间 */
  orderTimeStr: string;
  /** 订单信息 */
  payOrder: PayOrderInfo;
  /** 2 为拼团 */
  priceType?: number;
  picUrl: string;
  listPicUrl: string;
  totalVirtualList: EquityBrief[];
  /** 商品列表 */
  goodsList: OrderShortData[];
  /** 订单支付金额 */
  payPrice: number;
  /** 商品原价之和 */
  totalOriginalPrice: number;
  /** 商品销售价之和 */
  totalSalePrice: number;
  /** 支付截止时间 */
  deadlineTime: number;
  /** 是否两轮卡 */
  isTwoWheelCard: boolean;
  /** 扩展信息 */
  extra: Record<string, any>;
}

export interface OrderDetail extends OrderListItem {
  totalVirtualList: OrderEquity[];
  /** 是否支持退款 */
  orderCanRefund: boolean;
  /** 退款时间戳 */
  refundTime: string;
  /** 退款时间 */
  refundTimeStr: string;
  /** 过期时间戳 */
  deadlineTime: number;
  /** 过期时间 */
  deadlineTimeStr: string;
  /** 剩余数量 */
  remainNum: number;
  /** 待支付状态 */
  payStatus: number;
  /** 奖励金抵扣金额 */
  pointDiscountAmount: number;
  /** 剩余张数描述 */
  remainNumStr: string;

  /** 购买数量 */
  num: number;
  /** 优惠总金额 */
  totalPromoAmount?: number;
  /** 优惠信息 */
  totalPromotionInfos?: {
    /** 券ID */
    promotionId: string; // 券ID,当couponPromoType=1时存在
    /** 券名称 */
    promotionName: string; // 券名称
    /** 卡券商城优惠类型， 1-营销优惠 2-奖励金 */
    couponPromoType: number; // 卡券商城优惠类型， 1-营销优惠 2-奖励金
    /** 优惠金额 */
    worth: number; // 优惠金额
    /** 可用积分数量，当couponPromoType=2时存在 */
    points: number;
  }[];
  totalCouponsList: any[];
}

export interface OrderState {
  curOrder: OrderData | null;
  orderList: OrderShortData[];
  hasMore: boolean;
  curPage: number;
  allOrderList: any;
  effectiveOrderList: any;
}

// initial state
const initState: OrderState = {
  curOrder: null,
  orderList: [],
  hasMore: true,
  curPage: 1,
  allOrderList: {
    orderList: [],
    hasMore: true,
    curPage: 1,
  },
  effectiveOrderList: {
    orderList: [],
    hasMore: true,
    curPage: 1,
  },
};

// getters
const getters = {};

let isRequestOrderList = false;
// actions
// TODO 新订单列表过滤掉已关闭的订单
const actions = {
  /**
   * 获取订单列表
   */
  actionRequestBuyList: async (
    { commit, state }: { commit: Commit; state: OrderState },
    {
      pageNum,
      activityId,
      showStatus,
      sceneCode,
      subSceneCode,
    }: {
      pageNum: number;
      activityId?: string;
      showStatus?: string;
      sceneCode?: string,
      subSceneCode?: string,
    },
  ) => {
    // return
    if (isRequestOrderList) {
      return;
    }

    let keyStr = 'allOrderList';
    if (showStatus === 'effective') {
      keyStr = 'effectiveOrderList';
    }

    isRequestOrderList = true;
    const finalData = {
      hasMore: state.hasMore,
      curPage: state.curPage,
      orderList: state[keyStr].orderList,
    };

    if (pageNum === 1) { // 如果是第一页则清空list
      finalData.orderList = [];
    }

    const req: ReqRequestBuyList = {
      pageNum,
      pageSize: 10,
      activityId,
      sceneCode,
      subSceneCode,
      needTwoWheelOrder: !activityId,
      showStatus,
      // 过滤已关闭订单
      filterClosedOrder: true,
    };

    let res: {
      pageCount: number // 总页数
      recordsCount: number // 总记录数
      pageSize: number // 每页数量
      pageNum: number // 当前页号
      orderList: OrderShortData[]
    } | null = null;

    try {
      ({ data: res } = await requestBuyList(req));
    } finally {
      isRequestOrderList = false;
    }

    if (res) {
      const {
        orderList,
        recordsCount,
      } = res;
      finalData.orderList = finalData.orderList.concat(orderList);
      finalData.curPage = pageNum;

      const count = recordsCount || 0;

      // 如果返回的有数据，且orderList的数量小于总数量，则还有数据
      if ((finalData.orderList.length < count) && orderList.length > 0) {
        finalData.hasMore = true;
      } else {
        finalData.hasMore = false;
      }
    }

    commit('setOrderState', {
      key: keyStr,
      value: finalData,
    });
  },
  /**
   * 当前订单详情数据
   */
  actionRequestBuyDetail: async ({ commit }: {
    commit: Commit;
    state: OrderState;
  }, orderId: string) => {
    let finalOrderId = orderId;
    if (finalOrderId.indexOf('&') > 0) {
      finalOrderId = finalOrderId.substring(0, finalOrderId.indexOf('&'));
    }
    const req: ReqRequestBuyDetail = {
      orderId: finalOrderId,
    };
    const res = await requestBuyDetail(req);
    const finalDetail: OrderData = res.data;
    commit('setDatas', {
      key: 'curOrder',
      value: finalDetail,
    });
  },
  resetBuyDetail: async ({ commit }: { commit: Commit; state: OrderState }) => {
    commit('setDatas', {
      key: 'curOrder',
      value: null,
    });
  },
};

interface OrderCellData<T extends keyof OrderState> {
  key: T;
  value: OrderState[T];
}

// mutations
const mutations = {
  setDatas: <T extends keyof OrderState>(
    state: OrderState,
    cellData: OrderCellData<T>,
  ) => {
    if (!cellData || !cellData.key) {
      return;
    }

    const { key } = cellData;
    state[key] = cellData.value;
  },
  setOrderState: <T extends keyof OrderState>(
    state: OrderState, newState: OrderCellData<T>) => {
    if (!newState || !newState.key) {
      return;
    }

    const { key } = newState;

    state[key] = newState.value;

    // const updater = <T extends keyof OrderState>(key: T, value: OrderState[T]) => {
    //   console.log(key);
    //   state[key] = value;
    // };
    // Object.entries(newState).forEach(([key, value]) => updater(
    //   key as keyof typeof newState,
    //   value,
    // ));
  },
};

export default {
  namespaced: true,
  state: initState,
  getters,
  actions,
  mutations,
};
